import { ErrorHandler, Injectable, inject, isDevMode } from '@angular/core';
import { LoggerService } from '@cargos/sprintpay-services';
import Swal from 'sweetalert2';

@Injectable()
export class ApplicationErrorHandlerService implements ErrorHandler {
    private readonly loggerService = inject(LoggerService);

    public handleError(error: unknown): void {
        const errorText = this.extractErrorMessage(error);
        const customError = this.normalizeToError(error);

        if (isDevMode()) {
            // eslint-disable-next-line no-console
            console.warn('Error occured:', error);
        }

        this.loggerService.captureException(customError);

        if (this.shouldIgnoreError(errorText)) {
            return;
        }

        Swal.fire({
            title: 'Error Ocurred',
            text: 'Something went wrong, please reload the page.',
            icon: 'error',
            showConfirmButton: true,
            confirmButtonText: 'Close',
            confirmButtonColor: '#007E59',
            allowOutsideClick: false,
        });
    }

    private extractErrorMessage(error: unknown): string {
        const candidate = this.unwrapRejection(error);

        if (typeof candidate === 'string') {
            return candidate;
        }

        if (candidate instanceof Error) {
            return candidate.message;
        }

        if (this.isRecord(candidate) && typeof candidate['message'] === 'string') {
            return candidate['message'];
        }

        return 'Unknown Error';
    }

    private normalizeToError(error: unknown): Error {
        const candidate = this.unwrapRejection(error);

        if (candidate instanceof Error) {
            return candidate;
        }

        if (typeof candidate === 'string') {
            return new Error(candidate);
        }

        if (this.isRecord(candidate)) {
            const message = this.extractMessageFromObject(candidate);
            const normalizedError = new Error(message);

            if (typeof candidate['name'] === 'string' && candidate['name'].length > 0) {
                normalizedError.name = candidate['name'];
            }

            return normalizedError;
        }

        return new Error('Unknown Error');
    }

    private extractMessageFromObject(errorObject: Record<string, unknown>): string {
        if (typeof errorObject['message'] === 'string' && errorObject['message'].length > 0) {
            return errorObject['message'];
        }

        const status = typeof errorObject['status'] === 'number' ? `status=${errorObject['status']}` : '';
        const statusText = typeof errorObject['statusText'] === 'string' ? errorObject['statusText'] : '';
        const url = typeof errorObject['url'] === 'string' ? errorObject['url'] : '';

        const composed = [status, statusText, url].filter(Boolean).join(' | ');
        if (composed.length > 0) {
            return composed;
        }

        const keys = Object.keys(errorObject);
        return keys.length > 0 ? `Non-Error object thrown with keys: ${keys.join(', ')}` : 'Non-Error object thrown';
    }

    private unwrapRejection(error: unknown): unknown {
        if (this.isRecord(error) && 'rejection' in error) {
            return error['rejection'];
        }

        return error;
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    private shouldIgnoreError(errorMessage: string): boolean {
        const keywordsToIgnore = ['radio_0'];
        return keywordsToIgnore.some((keyword) => errorMessage.includes(keyword));
    }
}
